import React from 'react';
import PropTypes from 'prop-types';

import AppAvatar from 'components/App/Avatar';
import { Input } from 'reactstrap';

import INITIAL_VALUE from 'constants/initialValue';

import SearchIcon from 'assets/images/search-icon.svg';

import styles from './MessageList.module.scss';

const SCROLL_LOAD_MORE_THRESHOLD = 80;

const MESSAGE_DATA_LINK_REGEX = /\[link\s+data-(?:booking|place)-id=["'][^"']+["']\](.*?)\[\/link\]/gi;

const isFileText = (text) => {
  if (!text || typeof text !== 'string') {
    return false;
  }

  const trimmedText = text.trim();

  if (!trimmedText.startsWith('{')) {
    return false;
  }

  try {
    const parsed = JSON.parse(trimmedText);

    return Boolean(parsed?.url && parsed?.name);
  } catch {
    return false;
  }
};

const getPlainMessageText = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  return text.replace(MESSAGE_DATA_LINK_REGEX, '$1');
};

const getLastMessagePreview = (lastMessage) => {
  if (!lastMessage) {
    return 'Chưa có tin nhắn';
  }

  let content;

  if (lastMessage.image) {
    content = 'Đã gửi 1 ảnh';
  } else if (isFileText(lastMessage.text)) {
    content = 'Đã gửi 1 tệp đính kèm';
  } else {
    content = getPlainMessageText(lastMessage.text);
  }

  return lastMessage.place ? `Bạn: ${content}` : content;
};

function MessageList(props) {
  const {
    listChatRooms,
    selectedId,
    keyword,
    isUnreadOnly,
    onSelect,
    onSearch,
    onToggleUnreadOnly,
    onLoadMore,
    isLoadingMore,
    hasMore,
    className,
  } = props;

  const handleScroll = React.useCallback((event) => {
    if (!hasMore || isLoadingMore) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - scrollTop - clientHeight <= SCROLL_LOAD_MORE_THRESHOLD) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  return (
    <div className={`${styles.MessageList} ${className || ''}`}>
      <div className={styles.Header}>
        <h3 className={styles.Title}>Tin nhắn</h3>
        <div className={styles.UnreadFilter}>
          <input
            type="checkbox"
            id="unread"
            checked={isUnreadOnly}
            onChange={(event) => onToggleUnreadOnly(event.target.checked)}
          />
          <label htmlFor="unread">Chưa đọc</label>
        </div>
      </div>

      <div className={styles.SearchBox}>
        <div className={styles.SearchContainer}>
          <Input
            placeholder="Tìm theo tên, số điện thoại, địa chỉ email"
            className={styles.SearchInput}
            value={keyword}
            onChange={(event) => onSearch(event.target.value)}
          />

          <img
            src={SearchIcon}
            alt="search"
            className={styles.SearchIcon}
          />
        </div>
      </div>

      <div className={styles.ConversationList} onScroll={handleScroll}>
        {listChatRooms.map((chatRoom) => {
          const customerName = chatRoom.user?.fullName || '';

          return (
            <button
              key={chatRoom._id}
              type="button"
              className={`${styles.ConversationItem} ${chatRoom._id === selectedId ? styles.Active : ''}`}
              onClick={() => onSelect(chatRoom)}
            >
              <AppAvatar
                name={customerName}
                size={40}
                className={styles.Avatar}
              />
              <div className={styles.ConversationBody}>
                <p className={styles.CustomerRow}>
                  <strong>{customerName}</strong>
                  {chatRoom.booking && (
                    <span>
                      {' - '}
                      {chatRoom.booking.orderId}
                    </span>
                  )}
                </p>
                <p className={styles.Preview}>{getLastMessagePreview(chatRoom.lastMessage)}</p>
              </div>
              {!chatRoom.isRead && <span className={styles.UnreadDot} />}
            </button>
          );
        })}

        {isLoadingMore && (
          <div className={styles.LoadingMore}>Đang tải...</div>
        )}
      </div>
    </div>
  );
}

MessageList.propTypes = {
  listChatRooms: PropTypes.instanceOf(Array),
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  keyword: PropTypes.string,
  isUnreadOnly: PropTypes.bool,
  onSelect: PropTypes.func,
  onSearch: PropTypes.func,
  onToggleUnreadOnly: PropTypes.func,
  onLoadMore: PropTypes.func,
  isLoadingMore: PropTypes.bool,
  hasMore: PropTypes.bool,
  className: PropTypes.string,
};

MessageList.defaultProps = {
  listChatRooms: [],
  selectedId: null,
  keyword: '',
  isUnreadOnly: false,
  onSelect: INITIAL_VALUE.FUNCTION,
  onSearch: INITIAL_VALUE.FUNCTION,
  onToggleUnreadOnly: INITIAL_VALUE.FUNCTION,
  onLoadMore: INITIAL_VALUE.FUNCTION,
  isLoadingMore: false,
  hasMore: false,
  className: '',
};

export default MessageList;
